import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ArrowRight, Package, Truck, Car, Caravan, Container, ShieldCheck, MapPin, Clock, CreditCard, Star, ChevronDown, LayoutDashboard } from "lucide-react";
import { motion } from "framer-motion";
import { TiltCard3D } from "../components/motion/MotionPremium";

const STEPS = [
  {
    n: "01",
    t: "Créez votre compte",
    shortD: "Inscrivez-vous comme expéditeur ou transporteur. KYC strict pour garantir la confiance.",
    icon: ShieldCheck,
    details: [
      "Choisissez votre rôle : Expéditeur (particulier ou entreprise) ou entreprise de transport établie en France.",
      "Inscription en 2 minutes avec email + mot de passe. Téléphone et société (SIRET, KBIS) pour les pros.",
      "Pour les transporteurs : documents d'entreprise et de transport, identité et permis des chauffeurs, véhicules et assurances.",
      "Une pré-analyse technique peut aider à détecter les dossiers incomplets ; la décision d'activation reste humaine.",
      "Tant que le dossier n'est pas validé, l'entreprise ne peut pas candidater à une mission.",
    ],
  },
  {
    n: "02",
    t: "Matching automatique",
    shortD: "Les transporteurs compatibles reçoivent la mission : véhicule, zone, documents validés.",
    icon: MapPin,
    details: [
      "Publiez une mission en moins d'une minute : adresse départ/arrivée, date, heure d'enlèvement, poids, volume, type de véhicule, prix proposé.",
      "La distance routière est estimée par le service cartographique ; une distance à vol d'oiseau sert seulement de solution de repli.",
      "Notre algorithme filtre les transporteurs : véhicule compatible (voiture, utilitaire, camion, poids lourd), zone géographique (rayon paramétré par chaque transporteur), documents KYC validés.",
      "Seuls les transporteurs pertinents voient votre mission dans leur feed — triée par distance pour éviter les kilomètres à vide.",
      "Notifications dès qu'une mission correspond à leur profil : plus besoin de chercher, tout arrive automatiquement.",
    ],
  },
  {
    n: "03",
    t: "Livrez & soyez payé",
    shortD: "Paiement après sélection, suivi en temps réel, reversement après livraison vérifiée.",
    icon: CreditCard,
    details: [
      "Quand l'expéditeur choisit nominativement un transporteur, il paie via Stripe Checkout.",
      "Le paiement est encaissé dans le circuit Stripe Connect puis conservé avant reversement, selon les conditions du prestataire.",
      "Suivi temps réel des 5 statuts : publiée → acceptée → chargée → en route → livrée. Messagerie intégrée.",
      "Après preuve de remise et confirmation de l'expéditeur, MML déclenche le reversement de 85 % au transporteur ; un incident bloque le flux pour revue.",
      "Notation bilatérale (1 à 5 étoiles) : expéditeur et transporteur s'évaluent. La confiance est mesurée, les mauvais acteurs écartés.",
    ],
  },
];

const TRUST_BLOCKS = [
  {
    id: "kyc",
    icon: ShieldCheck,
    t: "KYC strict",
    shortD: "Pièce d'identité, permis, carte grise, assurance, KBIS — vérifiés par notre équipe.",
    details: [
      "Chaque transporteur téléverse sa pièce d'identité (recto + verso) et son permis (recto + verso) avec la catégorie exacte (B1, B, C1E, C, CE) correspondant à son véhicule déclaré.",
      "Les fichiers sont protégés et leur intégrité est contrôlée ; toute anomalie conduit à une demande de justificatif ou à une revue humaine.",
      "Aucun outil automatisé n'accorde seul l'accès aux missions.",
      "SIRET, justificatif d'immatriculation, licence de transport, assurances et justificatifs adaptés au véhicule sont contrôlés.",
      "Chaque document a une date d'expiration. L'admin valide manuellement chaque pièce. Un permis non conforme ou un document périmé bloque immédiatement les missions.",
    ],
  },
  {
    id: "tracking",
    icon: Clock,
    t: "Suivi temps réel",
    shortD: "De la publication à la livraison : acceptée, chargée, en route, livrée.",
    details: [
      "Chaque mission suit 5 statuts : publiée → acceptée → en route → chargée → livrée. Chaque changement est horodaté et conservé dans l'historique.",
      "L'expéditeur voit où en est sa marchandise. Le transporteur ne peut démarrer que lorsque Stripe a confirmé le paiement.",
      "Messagerie intégrée mission par mission : expéditeur et transporteur échangent en direct, sans partager leur numéro personnel.",
      "Notifications in-app à chaque étape : acceptation, paiement confirmé, départ, chargement, livraison.",
      "Tous les échanges et statuts sont archivés pour servir de preuve en cas de litige. L'admin peut consulter l'historique complet d'une mission.",
    ],
  },
  {
    id: "rating",
    icon: Star,
    t: "Notation bilatérale",
    shortD: "Expéditeurs et transporteurs se notent. La confiance est mesurée.",
    details: [
      "Après chaque livraison, les deux parties se notent de 1 à 5 étoiles avec un commentaire libre. Impossible de noter avant la livraison confirmée.",
      "La note moyenne d'un transporteur est visible publiquement : les mauvais acteurs sont rapidement écartés, les bons sont valorisés dans le matching.",
      "Les expéditeurs aussi sont notés : un mauvais payeur, un contact difficile ou une marchandise non conforme à la description impactera leur réputation.",
      "Les commentaires détaillés sont stockés pour permettre aux autres membres de faire un choix informé avant d'accepter une mission.",
      "L'admin peut modérer les avis en cas d'abus ou de litige manifeste, garantissant l'intégrité du système de confiance.",
    ],
  },
];

function StepsGrid() {
  const [openStep, setOpenStep] = useState(null);
  return (
    <div className="grid md:grid-cols-3 gap-4">
      {STEPS.map((s) => {
        const isOpen = openStep === s.n;
        return (
          <button key={s.n}
            type="button"
            onClick={() => setOpenStep(isOpen ? null : s.n)}
            className={`text-left p-8 rounded-2xl transition-all duration-300 ease-out border ${
              isOpen
                ? "bg-neutral-950 text-white border-neutral-950 md:col-span-3"
                : "bg-neutral-50 hover:bg-neutral-100 border-transparent"
            }`}
            data-testid={`step-${s.n}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className={`font-mono text-xs mb-6 ${isOpen ? "text-white/50" : "text-neutral-400"}`}>{s.n}</div>
                <s.icon className={`w-6 h-6 mb-4 ${isOpen ? "text-white" : ""}`} strokeWidth={1.8} />
                <h3 className="font-display text-xl md:text-2xl font-black tracking-tight mb-2">{s.t}</h3>
                <p className={`text-sm ${isOpen ? "text-white/70" : "text-neutral-600"}`}>{s.shortD}</p>
              </div>
              <ChevronDown className={`w-5 h-5 shrink-0 mt-1 transition-transform duration-300 ${isOpen ? "rotate-180 text-white" : "text-neutral-400"}`} />
            </div>
            {isOpen && (
              <div className="mt-8 pt-8 border-t border-white/20 grid md:grid-cols-2 gap-x-10 gap-y-4" data-testid={`step-${s.n}-details`}>
                {s.details.map((d, i) => (
                  <div key={`${s.n}-${i}-${d.slice(0,32)}`} className="flex items-start gap-3">
                    <span className="font-mono text-xs text-white/40 mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-sm text-white/80 leading-relaxed">{d}</p>
                  </div>
                ))}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function TrustGrid() {
  const [openId, setOpenId] = useState(null);
  const refs = React.useRef({});
  const handleToggle = (id) => {
    const willOpen = openId !== id;
    setOpenId(willOpen ? id : null);
    if (willOpen) {
      setTimeout(() => {
        refs.current[id]?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
    }
  };
  return (
    <div className="grid md:grid-cols-3 gap-4">
      {TRUST_BLOCKS.map((b) => {
        const isOpen = openId === b.id;
        return (
          <button key={b.id}
            type="button"
            ref={(el) => (refs.current[b.id] = el)}
            onClick={() => handleToggle(b.id)}
            className={`text-left p-8 rounded-2xl transition-all duration-300 ease-out border ${
              isOpen
                ? "bg-neutral-950 text-white border-neutral-950 md:col-span-3"
                : "bg-white hover:bg-neutral-50 border-neutral-200"
            }`}
            data-testid={`trust-${b.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <b.icon className={`w-6 h-6 mb-4 ${isOpen ? "text-white" : ""}`} strokeWidth={1.8} />
                <h3 className="font-display text-xl md:text-2xl font-black tracking-tight mb-2">{b.t}</h3>
                <p className={`text-sm ${isOpen ? "text-white/70" : "text-neutral-600"}`}>{b.shortD}</p>
              </div>
              <ChevronDown className={`w-5 h-5 shrink-0 mt-1 transition-transform duration-300 ${isOpen ? "rotate-180 text-white" : "text-neutral-400"}`} />
            </div>
            {isOpen && (
              <div className="mt-8 pt-8 border-t border-white/20 grid md:grid-cols-2 gap-x-10 gap-y-4" data-testid={`trust-${b.id}-details`}>
                {b.details.map((d, i) => (
                  <div key={`${b.id}-${i}-${d.slice(0,32)}`} className="flex items-start gap-3">
                    <span className="font-mono text-xs text-white/40 mt-0.5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-sm text-white/80 leading-relaxed">{d}</p>
                  </div>
                ))}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const dashboardPath = user?.role === "admin" ? "/admin"
    : user ? "/shipper" : "/";
  return (
    <div data-testid="landing-page" className="bg-white">
      {/* HERO — text-focused, sober */}
      <section className="relative overflow-hidden border-b border-[#1F4032]/10 bg-white">
        {/* Glow blobs décoratifs */}
        <div aria-hidden className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#1F4032]/10 to-transparent blur-3xl pointer-events-none" />
        <div aria-hidden className="absolute top-1/2 -left-20 w-[400px] h-[400px] rounded-full bg-gradient-to-tr from-[#1F4032]/8 to-transparent blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 lg:py-36">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-3xl"
          >
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] font-black leading-[0.92] tracking-tighter text-[#15302A]" data-testid="hero-title">
              Expédiez<br />
              partout.<br />
              <span className="bg-gradient-to-br from-[#2A5443] via-[#1F4032] to-[#0F2A20] bg-clip-text text-transparent">Livrez en confiance.</span>
            </h1>
            <p className="text-lg md:text-xl text-neutral-600 mt-8 max-w-xl">
              La marketplace logistique qui connecte expéditeurs et transporteurs vérifiés.
              Publiez une mission. Candidatez à une mission. Tout se passe ici.
            </p>
            <div className="flex flex-wrap gap-3 mt-10">
              {user ? (
                <>
                  <Link to={dashboardPath} data-testid="cta-dashboard-hero">
                    <Button className="rounded-full bg-[#1F4032] hover:bg-[#2A5443] text-white h-14 px-7 text-base font-medium gap-2">
                      <LayoutDashboard className="w-4 h-4" /> Mon dashboard <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  {user.role !== "admin" && (
                    <Link to="/missions/new">
                      <Button variant="outline" className="rounded-full border-neutral-300 text-black hover:bg-neutral-100 h-14 px-7 text-base font-medium">
                        Publier une mission
                      </Button>
                    </Link>
                  )}
                  <span className="flex items-center px-3 text-sm text-neutral-600">
                    Connecté en tant que <b className="ml-1 text-black">{user.name}</b>
                  </span>
                </>
              ) : (
                <>
                  <Link to="/register?role=shipper" data-testid="cta-shipper">
                    <Button className="rounded-full bg-[#1F4032] hover:bg-[#2A5443] text-white h-14 px-7 text-base font-medium gap-2">
                      Envoyer un colis <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to="/register?role=transporter" data-testid="cta-transporter">
                    <Button variant="outline" className="rounded-full border-neutral-300 text-black hover:bg-neutral-100 h-14 px-7 text-base font-medium">
                      Devenir transporteur
                    </Button>
                  </Link>
                  <Link to="/login" className="flex items-center px-3 text-sm text-neutral-600 hover:text-black underline underline-offset-4">
                    J&apos;ai déjà un compte
                  </Link>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* TWO-SIDED VALUE PROP avec tilt 3D */}
      <section className="border-b border-neutral-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="grid md:grid-cols-2 gap-10 md:gap-16">
            <TiltCard3D intensity={6} className="p-8 md:p-10 border border-[#1F4032]/10 rounded-2xl bg-white shadow-lg shadow-[#1F4032]/5">
              <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center mb-6">
                <Package className="w-5 h-5 text-black" strokeWidth={2} />
              </div>
              <div className="overline mb-3">Expéditeurs</div>
              <h2 className="font-display text-3xl font-black tracking-tighter mb-4">
                Publiez une mission en 2 minutes.
              </h2>
              <p className="text-neutral-600 mb-6">
                Décrivez votre envoi, fixez le prix, recevez des transporteurs vérifiés. Paiement sécurisé.
              </p>
              <Link to="/register?role=shipper">
                <Button variant="ghost" className="rounded-full px-0 hover:bg-transparent hover:underline gap-2 font-semibold">
                  Publier une mission <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </TiltCard3D>
            <TiltCard3D intensity={6} className="p-8 md:p-10 border border-[#1F4032] rounded-2xl bg-gradient-to-br from-[#1F4032] via-[#15302A] to-[#0A1812] text-white shadow-2xl shadow-[#1F4032]/30">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mb-6 backdrop-blur">
                <Truck className="w-5 h-5 text-white" strokeWidth={2} />
              </div>
              <div className="overline mb-3 text-white/60">Transporteurs</div>
              <h2 className="font-display text-3xl font-black tracking-tighter mb-4">
                Choisissez vos missions.
              </h2>
              <p className="text-white/70 mb-6">
                Filtrez par véhicule, distance et prix. Candidatez aux missions qui vous conviennent ; l'expéditeur choisit le transporteur.
              </p>
              <Link to="/register?role=transporter">
                <Button variant="ghost" className="rounded-full px-0 hover:bg-transparent hover:underline gap-2 font-semibold text-white hover:text-white">
                  Commencer à rouler <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </TiltCard3D>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter mb-4 max-w-2xl">
            Trois étapes.<br />
            <span className="text-neutral-400">Zéro friction.</span>
          </h2>
          <p className="text-sm text-neutral-500 mb-10">Cliquez sur une étape pour en savoir plus.</p>
          <StepsGrid />
        </div>
      </section>

      {/* VEHICLES */}
      <section className="border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="flex flex-wrap justify-between items-end gap-4 mb-12">
            <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter">
              De la voiture<br />au poids lourd.
            </h2>
            <p className="text-neutral-500 max-w-sm text-sm">
              Chaque transporteur définit son type de véhicule et son rayon d&apos;action. Le matching fait le reste.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { t: "Voiture", icon: Car, size: "w-6 h-6" },
              { t: "Utilitaire", icon: Caravan, size: "w-7 h-7" },
              { t: "Camion", icon: Truck, size: "w-8 h-8" },
              { t: "Poids lourd", icon: Container, size: "w-9 h-9" },
            ].map((v) => (
              <div key={v.t} className="p-6 rounded-2xl border border-neutral-200 hover:border-neutral-400 transition-colors">
                <v.icon className={`${v.size} mb-4 text-neutral-700`} strokeWidth={1.6} />
                <div className="font-display font-black text-lg tracking-tight">{v.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="border-b border-neutral-200">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter mb-4">
            La confiance,<br />
            <span className="text-neutral-400">mesurée à chaque étape.</span>
          </h2>
          <p className="text-sm text-neutral-500 mb-10">Cliquez sur un pilier pour le détail.</p>
          <TrustGrid />
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-neutral-950 text-white">
        <div className="max-w-6xl mx-auto px-6 py-24 md:py-32">
          <h2 className="font-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] max-w-3xl mb-10">
            Envoyez.<br />
            Roulez.<br />
            <span className="text-neutral-500">Commencez maintenant.</span>
          </h2>
          <div className="flex flex-wrap gap-3">
            {user ? (
              <Link to={dashboardPath} data-testid="cta-dashboard-bottom">
                <Button className="rounded-full bg-white text-black hover:bg-neutral-200 h-14 px-7 text-base font-medium gap-2">
                  <LayoutDashboard className="w-4 h-4" /> Aller à mon dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/register" data-testid="cta-bottom">
                  <Button className="rounded-full bg-white text-black hover:bg-neutral-200 h-14 px-7 text-base font-medium">
                    Créer un compte <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="rounded-full border-white/30 text-white bg-transparent hover:bg-white/10 h-14 px-7 text-base font-medium">
                    Se connecter
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </section>

      <footer className="bg-neutral-950 text-white/50 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-wrap justify-between gap-4 text-xs">
          <div>© 2026 MML — Plateforme Logistique</div>
          <Link to="/contact" className="font-mono hover:text-white">Nous contacter</Link>
        </div>
      </footer>
    </div>
  );
}
